/**
 * usePublishedListings Hook
 *
 * React Query hooks for partner marketplace publishing operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { partnerMarketplaceService } from '../../../api/marketplace.service';
import type {
  MarketplaceListing,
  MarketplaceListingCreateDTO,
  MarketplaceListingUpdateDTO,
  MarketplaceVersionCreateDTO,
} from '../../../types/marketplace.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Hook for checking if tenant is a marketplace partner
 */
export const usePartnerStatus = (
  tenantId: string
): UseQueryResult<{ isPartner: boolean }, Error> => {
  return useQuery<{ isPartner: boolean }, Error>({
    queryKey: ['marketplace', 'partner-status', tenantId],
    queryFn: async () => {
      console.log('[usePartnerStatus] Checking partner status for tenant:', tenantId);
      return await partnerMarketplaceService.getPartnerStatus(tenantId);
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes - partner status doesn't change often
    retry: 1, // Only retry once on failure
  });
};

/**
 * Hook for fetching listings published by this tenant
 */
export const usePublishedListings = (
  tenantId: string
): UseQueryResult<MarketplaceListing[], Error> => {
  return useQuery<MarketplaceListing[], Error>({
    queryKey: ['marketplace', 'published', tenantId],
    queryFn: async () => {
      console.log('[usePublishedListings] Fetching published listings for tenant:', tenantId);
      return await partnerMarketplaceService.listPublishedApps(tenantId);
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for publishing an application to the marketplace
 */
export const usePublishToMarketplace = (): UseMutationResult<
  MarketplaceListing,
  Error,
  { tenantId: string; data: MarketplaceListingCreateDTO }
> => {
  const queryClient = useQueryClient();

  return useMutation<MarketplaceListing, Error, { tenantId: string; data: MarketplaceListingCreateDTO }>({
    mutationFn: async ({ tenantId, data }) => {
      console.log('[usePublishToMarketplace] Publishing app:', data.sourceApplicationId);
      return await partnerMarketplaceService.publishApp(tenantId, data);
    },
    onSuccess: (_, { tenantId }) => {
      // Invalidate published listings
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'published', tenantId] });
      // Invalidate global marketplace listings
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'listings'] });
    },
    onError: (error) => {
      console.error('[usePublishToMarketplace] Publish failed:', getErrorMessage(error));
    },
  });
};

/**
 * Hook for updating a marketplace listing
 */
export const useUpdateMarketplaceListing = (): UseMutationResult<
  MarketplaceListing,
  Error,
  { tenantId: string; listingId: string; data: MarketplaceListingUpdateDTO }
> => {
  const queryClient = useQueryClient();

  return useMutation<MarketplaceListing, Error, { tenantId: string; listingId: string; data: MarketplaceListingUpdateDTO }>({
    mutationFn: async ({ tenantId, listingId, data }) => {
      console.log('[useUpdateMarketplaceListing] Updating listing:', listingId);
      return await partnerMarketplaceService.updateListing(tenantId, listingId, data);
    },
    onSuccess: (_, { tenantId, listingId }) => {
      // Invalidate specific listing
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'listing', listingId] });
      // Invalidate published listings
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'published', tenantId] });
      // Invalidate global marketplace listings
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'listings'] });
    },
    onError: (error) => {
      console.error('[useUpdateMarketplaceListing] Update failed:', getErrorMessage(error));
    },
  });
};

/**
 * Hook for adding a new version to a listing
 */
export const useAddMarketplaceVersion = (): UseMutationResult<
  MarketplaceListing,
  Error,
  { tenantId: string; listingId: string; data: MarketplaceVersionCreateDTO }
> => {
  const queryClient = useQueryClient();

  return useMutation<MarketplaceListing, Error, { tenantId: string; listingId: string; data: MarketplaceVersionCreateDTO }>({
    mutationFn: async ({ tenantId, listingId, data }) => {
      console.log('[useAddMarketplaceVersion] Adding version to listing:', listingId);
      return await partnerMarketplaceService.addVersion(tenantId, listingId, data);
    },
    onSuccess: (_, { tenantId, listingId }) => {
      // Invalidate specific listing
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'listing', listingId] });
      // Invalidate published listings
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'published', tenantId] });
      // Invalidate global marketplace listings
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'listings'] });
    },
    onError: (error) => {
      console.error('[useAddMarketplaceVersion] Add version failed:', getErrorMessage(error));
    },
  });
};

/**
 * Hook for unpublishing a marketplace listing
 */
export const useUnpublishFromMarketplace = (): UseMutationResult<
  MarketplaceListing,
  Error,
  { tenantId: string; listingId: string }
> => {
  const queryClient = useQueryClient();

  return useMutation<MarketplaceListing, Error, { tenantId: string; listingId: string }>({
    mutationFn: async ({ tenantId, listingId }) => {
      console.log('[useUnpublishFromMarketplace] Unpublishing listing:', listingId);
      return await partnerMarketplaceService.unpublishListing(tenantId, listingId);
    },
    onSuccess: (_, { tenantId, listingId }) => {
      // Invalidate specific listing
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'listing', listingId] });
      // Invalidate published listings
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'published', tenantId] });
      // Invalidate global marketplace listings
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'listings'] });
    },
    onError: (error) => {
      console.error('[useUnpublishFromMarketplace] Unpublish failed:', getErrorMessage(error));
    },
  });
};

/**
 * Hook for fetching a single listing as the publisher (any status)
 */
export const usePublisherListing = (
  tenantId: string,
  listingId: string
): UseQueryResult<MarketplaceListing, Error> => {
  return useQuery<MarketplaceListing, Error>({
    queryKey: ['marketplace', 'publisher-listing', tenantId, listingId],
    queryFn: async () => {
      console.log('[usePublisherListing] Fetching listing:', listingId);
      return await partnerMarketplaceService.getListing(tenantId, listingId);
    },
    enabled: !!tenantId && !!listingId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for republishing a marketplace listing
 */
export const useRepublishToMarketplace = (): UseMutationResult<
  MarketplaceListing,
  Error,
  { tenantId: string; listingId: string }
> => {
  const queryClient = useQueryClient();

  return useMutation<MarketplaceListing, Error, { tenantId: string; listingId: string }>({
    mutationFn: async ({ tenantId, listingId }) => {
      console.log('[useRepublishToMarketplace] Republishing listing:', listingId);
      return await partnerMarketplaceService.republishListing(tenantId, listingId);
    },
    onSuccess: (_, { tenantId, listingId }) => {
      // Invalidate specific listing
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'listing', listingId] });
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'publisher-listing', tenantId, listingId] });
      // Invalidate published listings
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'published', tenantId] });
      // Invalidate global marketplace listings
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'listings'] });
    },
    onError: (error) => {
      console.error('[useRepublishToMarketplace] Republish failed:', getErrorMessage(error));
    },
  });
};
